// src/app/components/reporting/components/report-draft.tsx
//
// The composed report, and the confidence meter that goes with it.
//
// Source: handoff-reporting §5f and the "Report Draft" region of
// `Insights and Reports - Polished.dc.html`.
//
// ── The one thing this file exists for ─────────────────────────────────────
//
// `ReportSection.source_insight_ids` has been in the data contract since the
// module shipped and has never been rendered anywhere. It is the answer to the
// only question a reader has about an AI-composed paragraph — where did this
// come from — and it was sitting in the payload unread. Every section now ends
// in a "Built from" row, one pill per source, each pill coloured by the module
// the source came from.
//
// The pills are RESOLVED against the insight list rather than passed in, so a
// pill cannot name an insight the section does not cite, and an id whose
// insight has since been deleted renders as nothing rather than as a grey
// placeholder for a thing nobody can look at.

import { Pencil, Sparkles } from "lucide-react";
import { Button, Icon } from "@/app/components/kit";
import { resolveSources, shortLabel } from "../module-identity";
import { sentenceCase } from "../format";
import { ExportActions } from "./export-actions";
import type { GeneratedReport } from "../types";
import type { SavedInsight } from "../types";

/* ── the confidence meter ───────────────────────────────────────────────────
   Replaces "89% confidence" set in 11px grey beside the section count.

   The figure and the fill are ONE number. `confidence` goes in, the width and
   the printed percentage both come out of it, and there is no second prop that
   could disagree with the first — which is this codebase's most-repeated
   defect and the reason the kit's own Progress derives its caption too. */

export function ConfidenceMeter({
  confidence,
  note,
  compact = false,
}: {
  confidence: number;
  note?: string;
  compact?: boolean;
}) {
  const pct = Math.round(Math.min(100, Math.max(0, confidence)));
  return (
    <div className={compact ? "upm-ir-conf upm-ir-conf--compact" : "upm-ir-conf"}>
      <div className="upm-ir-conf__row">
        <span className="upm-ir-conf__label">Confidence</span>
        <span className="upm-ir-conf__value">{pct}%</span>
      </div>
      <div
        className="upm-ir-conf__track"
        role="progressbar"
        aria-label="Report confidence"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span className="upm-ir-conf__fill" style={{ width: `${pct}%` }} />
      </div>
      {note ? <span className="upm-ir-conf__note">{note}</span> : null}
    </div>
  );
}

/* ── the draft ────────────────────────────────────────────────────────────── */

export interface ReportDraftProps {
  report: GeneratedReport;
  /** Every saved insight, so a section's source ids can be resolved to titles
      and module colours. Without it the provenance row cannot be true. */
  insights: readonly SavedInsight[];
  onEdit?: () => void;
  onRefine?: () => void;
}

export function ReportDraft({ report, insights, onEdit, onRefine }: ReportDraftProps) {
  const sourceCount = new Set(
    report.sections.flatMap((s) => s.source_insight_ids ?? []),
  ).size;

  return (
    <article className="upm-ir-draft">
      <div className="upm-ir-draft__rule" />

      <header className="upm-ir-draft__head">
        <div className="upm-ir-draft__headmain">
          <div className="upm-ir-draft__mark">
            <span className="upm-ir-draft__dot" aria-hidden="true" />
            <span className="upm-ir-eyebrow">AI-composed draft</span>
          </div>
          <h3 className="upm-ir-draft__title">{report.title}</h3>
          <div className="upm-ir-draft__chips">
            <span className="upm-ir-chip upm-ir-chip--brand">{sentenceCase(report.format)}</span>
            <span className="upm-ir-chip upm-ir-chip--reason">{sentenceCase(report.audience)}</span>
            <span>
              {report.sections.length}
              {report.sections.length === 1 ? " section" : " sections"}
              {/* The source count is COUNTED off the sections, not read from
                  `insight_count`. The two answer different questions — how many
                  insights the request carried, versus how many the draft
                  actually cites — and printing the first beside the pills that
                  show the second is how a page states two numbers for one
                  thing. */}
              {sourceCount > 0
                ? ` · ${sourceCount}${sourceCount === 1 ? " source" : " sources"}`
                : ""}
            </span>
          </div>
        </div>

        <ConfidenceMeter
          confidence={report.confidence}
          note="Every claim traces to a saved measurement."
        />
      </header>

      <section className="upm-ir-draft__summary">
        <div className="upm-ir-draft__summary-eyebrow">Executive summary</div>
        <p className="upm-ir-draft__summary-body">{report.executive_summary}</p>
      </section>

      <div className="upm-ir-sections">
        {report.sections.map((section, i) => {
          const sources = resolveSources(section.source_insight_ids ?? [], insights);
          return (
            <section className="upm-ir-section" key={`${section.title}-${i}`}>
              {/* The editorial index is derived from position, so it cannot
                  disagree with the order the sections are in. */}
              <div className="upm-ir-section__idx" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="upm-ir-section__main">
                <div className="upm-ir-section__head">
                  <h4 className="upm-ir-section__title">{section.title}</h4>
                  {section.key_metric ? (
                    <span className="upm-ir-chip upm-ir-chip--metric">{section.key_metric}</span>
                  ) : null}
                </div>
                <p className="upm-ir-section__body">{section.content}</p>

                {sources.length > 0 ? (
                  <div className="upm-ir-prov">
                    <span className="upm-ir-prov__label">Built from</span>
                    {sources.map((s) => (
                      <span className="upm-ir-prov__pill" key={s.id}>
                        <span
                          className="upm-ir-prov__dot"
                          style={{ background: s.identity.color }}
                          aria-hidden="true"
                        />
                        {shortLabel(s.label)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>
          );
        })}
      </div>

      {(report.key_findings.length > 0 || report.recommendations.length > 0) && (
        <div className="upm-ir-columns">
          <div>
            <h4 className="upm-ir-columns__title">Key findings</h4>
            <div className="upm-ir-bullets">
              {report.key_findings.map((f, i) => (
                <p className="upm-ir-bullet" key={`f-${i}`}>
                  <span
                    className="upm-ir-bullet__dot upm-ir-bullet__dot--finding"
                    aria-hidden="true"
                  />
                  {f}
                </p>
              ))}
            </div>
          </div>
          <div>
            <h4 className="upm-ir-columns__title">Recommendations</h4>
            <div className="upm-ir-bullets">
              {report.recommendations.map((r, i) => (
                <p className="upm-ir-bullet" key={`r-${i}`}>
                  <span className="upm-ir-bullet__dot upm-ir-bullet__dot--rec" aria-hidden="true" />
                  {r}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      <footer className="upm-ir-rail">
        <span className="upm-ir-rail__label">Send it</span>
        <ExportActions report={report} />
        <span className="upm-ir-rail__end">
          {/* Editing is not in the DC, and it is not dropped either: the
              product can PUT a revised report and removing the only way in
              would strand a working endpoint. The rail is where report-level
              actions live, so it goes here rather than into the header the DC
              specifies exactly. */}
          {onEdit ? (
            <Button variant="secondary" icon={Pencil} onClick={onEdit}>
              Edit draft
            </Button>
          ) : null}
          {onRefine ? (
            <button type="button" className="upm-ir-refine" onClick={onRefine}>
              <Icon icon={Sparkles} size="sm" />
              Refine with Upmode
            </button>
          ) : null}
        </span>
      </footer>
    </article>
  );
}
